import { useState, type ReactNode } from "react";
import { Icon } from "../components/Icon.tsx";
import { Button, Skeleton, TableWrap } from "../components/ui.tsx";
import { t } from "../i18n/index.ts";
import { day, money } from "../lib/format.ts";
import { useAction } from "./manage/common.tsx";
import { downloadCsv, plain } from "./Reports.tsx";

/**
 * The tax return of a month or a quarter, box by box as the business's country files it (the
 * server's returns.ts): figures to copy into the tax office's portal.
 */

interface ReturnLine {
  code: string | null;
  label: string;
  base?: number | null;
  tax?: number | null;
  kind: "heading" | "line" | "total" | "result";
}
interface TaxReturnData {
  form: { id: string; name: string; authority: string };
  country: string;
  tax_name: string;
  from: string;
  to: string;
  lines: ReturnLine[];
  due: number;
  other_rates: number[];
  ledger_matches: boolean;
  notes: string[];
}

type Span = "month" | "quarter";

function periodOf(month: string, span: Span) {
  const [y, m] = month.split("-").map(Number) as [number, number];
  const first = span === "quarter" ? Math.floor((m - 1) / 3) * 3 + 1 : m;
  const last = span === "quarter" ? first + 2 : m;
  const from = `${y}-${String(first).padStart(2, "0")}-01`;
  const to = new Date(Date.UTC(y, last, 0)).toISOString().slice(0, 10);
  return { from, to };
}

export function TaxReturnView({
  month,
  onMonth,
  switcher,
}: {
  month: string;
  onMonth: (m: string) => void;
  switcher: ReactNode;
}) {
  const [span, setSpan] = useState<Span>("month");
  const { from, to } = periodOf(month, span);
  const { data } = useAction<TaxReturnData>("accounting.tax_return", {
    from,
    to,
  });
  const r = data && data.from === from && data.to === to ? data : null;
  // Forms without box numbers (a sales tax summary, a plain VAT return) need no box column.
  const boxes = !!r?.lines.some((l) => l.code);
  const csv = () =>
    r &&
    downloadCsv(
      `${r.form.id}-${from}-${to}.csv`,
      [t("tr.c_box"), t("tr.c_line"), t("tr.c_base"), t("tr.c_tax")],
      r.lines
        .filter((l) => l.kind !== "heading")
        .map((l) => [
          l.code ?? "",
          l.label,
          l.base == null ? "" : plain(l.base),
          l.tax == null ? "" : plain(l.tax),
        ]),
    );
  return (
    <div className="rep-grid">
      <div className="toolbar">
        {switcher}
        <div
          className="segmented"
          role="radiogroup"
          aria-label={t("tr.period")}
        >
          {(["month", "quarter"] as const).map((s) => (
            <button
              key={s}
              role="radio"
              aria-checked={span === s}
              className={span === s ? "active" : ""}
              onClick={() => setSpan(s)}
            >
              {t(`tr.span_${s}`)}
            </button>
          ))}
        </div>
        <label className="vat-month">
          <span>{span === "month" ? t("vat.month") : t("tr.quarter_of")}</span>
          <input
            className="input date-input"
            type="month"
            value={month}
            onChange={(e) =>
              /^\d{4}-\d{2}$/.test(e.target.value) && onMonth(e.target.value)
            }
          />
        </label>
      </div>
      {!r ? (
        <Skeleton h={360} r="var(--radius-lg)" />
      ) : (
        <>
          <section className="panel rep-wide tr" aria-labelledby="tr-title">
            <div className="panel-head">
              <div>
                <h2 id="tr-title">{r.form.name}</h2>
                <p className="muted small">
                  {t("rep.range", { from: day(r.from), to: day(r.to) })} ·{" "}
                  {r.form.authority}
                </p>
              </div>
              <Button variant="ghost" icon="download" onClick={csv}>
                {t("rep.download")}
              </Button>
            </div>
            <TableWrap label={r.form.name}>
              <table className="data-table tr-table">
                <thead>
                  <tr>
                    {boxes ? (
                      <th className="tr-code">{t("tr.c_box")}</th>
                    ) : null}
                    <th>{t("tr.c_line")}</th>
                    <th className="num">{t("tr.c_base")}</th>
                    <th className="num">{t("tr.c_tax")}</th>
                  </tr>
                </thead>
                <tbody>
                  {r.lines.map((l, i) =>
                    l.kind === "heading" ? (
                      <tr key={i} className="tr-heading">
                        <th colSpan={boxes ? 4 : 3} scope="colgroup">
                          {l.label}
                        </th>
                      </tr>
                    ) : (
                      <tr key={i} className={`tr-${l.kind}`}>
                        {boxes ? (
                          <td className="tr-code mono">{l.code ?? ""}</td>
                        ) : null}
                        <td>{l.label}</td>
                        <td className="num tabular">
                          {l.base == null ? "" : money(l.base)}
                        </td>
                        <td className="num tabular">
                          {l.tax == null ? "" : money(l.tax)}
                        </td>
                      </tr>
                    ),
                  )}
                </tbody>
              </table>
            </TableWrap>
          </section>
          <p className="callout callout-info rep-wide tr-due" role="status">
            <Icon name={r.due > 0 ? "bank" : "checkCircle"} size={16} />
            {r.due > 0
              ? t("tr.to_pay", { amount: money(r.due), tax: r.tax_name })
              : r.due < 0
                ? t("tr.credit", { amount: money(-r.due), tax: r.tax_name })
                : t("tr.nothing")}
          </p>
          {!r.ledger_matches ? (
            <p className="acc-check off rep-wide" role="status">
              <Icon name="alert" size={16} />
              {t("tr.ledger_off")}
            </p>
          ) : null}
          <ul className="tr-notes rep-wide">
            {r.notes.map((n) => (
              <li key={n} className="muted small">
                <Icon name="info" size={14} /> {n}
              </li>
            ))}
            <li className="muted small">
              <Icon name="info" size={14} /> {t("tr.not_filed")}
            </li>
          </ul>
        </>
      )}
    </div>
  );
}
